import { useCallback, useMemo } from 'react'

import { Link, Mail } from '@mui/icons-material'
import { Avatar, Typography } from '@mui/material'
import {
  SmepWorkspaceRoleRamInvitation,
  SmepWorkspaceRoleRamInvitationEditRolesEnum,
  SmepWorkspaceRoleRamMemberRolesEnum,
} from '@nativewaves/nw-cloud-sdk/smep'
import { ColumnDef } from '@tanstack/react-table'
import { formatDistanceToNow, parseISO } from 'date-fns'
import { useTranslation } from 'react-i18next'

import { RoleSelector } from 'components/RoleSelector'
import { ResolvedUser } from 'domains/Workspaces/Dashboard/ManageAccess/Invitations/'
import { useWorkspaceInvitationUpdateMutation } from 'hooks/mutations/smep'
import { workspaceSettingsPermissionsInvitationsRoute } from 'pages/NWPlatform/Workspaces/Workspace/Settings'

export const useColumns = () => {
  const { t } = useTranslation(['common', 'domain'])

  const { workspaceId } = workspaceSettingsPermissionsInvitationsRoute.useParams()

  const { mutate: updateInvitation } = useWorkspaceInvitationUpdateMutation()

  const handleUpdate = useCallback(
    ({ invitationId, roles }: { invitationId: string; roles: SmepWorkspaceRoleRamInvitationEditRolesEnum[] }) => {
      updateInvitation({
        invitationId,
        workspaceId,
        smepWorkspaceRoleRamInvitationEdit: {
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          //@ts-ignore
          roles,
        },
      })
    },
    [updateInvitation, workspaceId],
  )

  return useMemo<ColumnDef<SmepWorkspaceRoleRamInvitation>[]>(
    () => [
      {
        accessorKey: 'recipientType',
        header: () => null,
        meta: {
          sizing: {
            width: 64,
            px: 2,
          },
          align: 'center',
        },
        cell: ({ getValue }) => {
          const recipientType = getValue<SmepWorkspaceRoleRamInvitation['recipientType']>()

          return (
            <Avatar sx={{ width: 32, height: 32, bgcolor: 'background.levels.900' }}>
              {recipientType === 'Email' && <Mail fontSize="inherit" color="info" />}
              {recipientType === 'None' && <Link fontSize="inherit" color="info" />}
            </Avatar>
          )
        },
      },
      {
        accessorKey: 'recipientEmail',
        header: 'Invited User',
        meta: {
          sizing: {
            minWidth: 300,
          },
        },
        cell: ({ getValue, row }) => {
          const email = getValue<string | undefined>()
          const { recipientType } = row.original

          return (
            <Typography
              sx={{ color: (theme) => (!getValue() ? theme.palette.action.disabled : 'inherit') }}
              variant="inherit"
              noWrap
            >
              {recipientType === 'None' ? 'Link' : email}
            </Typography>
          )
        },
      },
      {
        accessorKey: 'changedByUserId',
        header: 'Created by',
        meta: {
          sizing: {
            minWidth: 120,
            maxWidth: 180,
          },
        },
        cell: ({ getValue }) => <ResolvedUser userId={`user-${getValue<string>()}`} />,
      },
      {
        accessorKey: 'expirationTime',
        header: t('expiresIn'),
        meta: {
          sizing: {
            minWidth: 120,
            maxWidth: 300,
          },
        },
        cell: ({ getValue }) => (
          <Typography
            sx={{ color: (theme) => (!getValue() ? theme.palette.action.disabled : 'inherit') }}
            variant="inherit"
            noWrap
          >
            {formatDistanceToNow(parseISO(getValue<string>()), { addSuffix: false })}
          </Typography>
        ),
      },
      {
        accessorKey: 'roles',
        header: () => null,
        meta: {
          align: 'end',
          sizing: {
            minWidth: 280,
            maxWidth: 320,
            px: 6,
          },
        },
        cell: ({ getValue, row }) => (
          <RoleSelector
            baseRoles={Object.values(SmepWorkspaceRoleRamMemberRolesEnum)}
            roles={[...getValue<Set<SmepWorkspaceRoleRamMemberRolesEnum>>()]}
            onUpdate={({ roles }) => handleUpdate({ invitationId: row.original.id, roles })}
          />
        ),
      },
    ],
    [handleUpdate, t],
  )
}
